import { Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  ShipmentEventType,
  ShipmentLocationType,
  TripShipmentLocation,
  useGetShipmentsByIdsQuery,
} from "../../../graphql/generated";
import { isPickupLocation } from "../../../utils/location/isPickupLocation";
import { getEffectiveShipmentIds } from "../../../utils/shipment/getEffectiveShipmentIds";
import { testIds } from "./ShipmentEventLogButton.testIds";

type CheckpointEventType =
  | ShipmentEventType.PickupCheckpointArrive
  | ShipmentEventType.PickupCheckpointLoadStart
  | ShipmentEventType.PickupCheckpointLeft
  | ShipmentEventType.DropOffCheckpointArrive
  | ShipmentEventType.DropoffCheckpointUnloadStart
  | ShipmentEventType.DropOffCheckpointLeft;

export type ShipmentEventLogButtonLocation = {
  _id?: string | null;
  locationType: ShipmentLocationType;
  shipmentIds?: string[] | null;
  shipmentId?: string | null;
};

export type ShipmentEventLogButtonProps = {
  shipmentLocation: ShipmentEventLogButtonLocation;
  onShipmentEventLog: (eventType: ShipmentEventType) => void;
  disabled?: boolean;
};

/**
 * Renders the next checkpoint event action (Arrive → Load/Unload → Depart)
 * for a shipment location, based on the events already logged on the
 * location's first shipment. Renders nothing once the location is departed
 * or when no shipment is attached to the location.
 */
const ShipmentEventLogButton = ({
  shipmentLocation,
  onShipmentEventLog,
  disabled,
}: ShipmentEventLogButtonProps) => {
  const { t } = useTranslation(["trips", "orders", "common"]);

  const effectiveShipmentIds = getEffectiveShipmentIds(
    shipmentLocation as Pick<TripShipmentLocation, "shipmentIds" | "shipmentId">,
  );
  const { data: shipmentsData } = useGetShipmentsByIdsQuery(
    {
      shipmentIds: effectiveShipmentIds,
    },
    {
      enabled: effectiveShipmentIds.length > 0,
    },
  );

  const firstShipment = shipmentsData?.shipments?.data?.[0] || null;

  const labelByEventType: { [key in CheckpointEventType]: string } = {
    [ShipmentEventType.PickupCheckpointArrive]: t("arrived", "Arrived"),
    [ShipmentEventType.PickupCheckpointLoadStart]: t("load", "Load"),
    [ShipmentEventType.PickupCheckpointLeft]: t("depart", "Depart"),
    [ShipmentEventType.DropOffCheckpointArrive]: t("arrived", "Arrived"),
    [ShipmentEventType.DropoffCheckpointUnloadStart]: t("unload", "Unload"),
    [ShipmentEventType.DropOffCheckpointLeft]: t("depart", "Depart"),
  };

  const getNextEventType = (): CheckpointEventType | null => {
    if (!firstShipment?.events) {
      return null;
    }

    const events = firstShipment.events.filter(
      (event) => event.shipmentLocation === shipmentLocation._id,
    );
    const arrivedEvent = events.find(
      (event) =>
        event.type === ShipmentEventType.PickupCheckpointArrive ||
        event.type === ShipmentEventType.DropOffCheckpointArrive,
    );

    const loadStartEvent = events.find(
      (event) =>
        event.type === ShipmentEventType.PickupCheckpointLoadStart ||
        event.type === ShipmentEventType.DropoffCheckpointUnloadStart,
    );

    const leftEvent = events.find(
      (event) =>
        event.type === ShipmentEventType.PickupCheckpointLeft ||
        event.type === ShipmentEventType.DropOffCheckpointLeft,
    );

    if (leftEvent) {
      return null;
    } else if (loadStartEvent) {
      return isPickupLocation(shipmentLocation)
        ? ShipmentEventType.PickupCheckpointLeft
        : ShipmentEventType.DropOffCheckpointLeft;
    } else if (arrivedEvent) {
      return isPickupLocation(shipmentLocation)
        ? ShipmentEventType.PickupCheckpointLoadStart
        : ShipmentEventType.DropoffCheckpointUnloadStart;
    } else {
      return isPickupLocation(shipmentLocation)
        ? ShipmentEventType.PickupCheckpointArrive
        : ShipmentEventType.DropOffCheckpointArrive;
    }
  };

  const nextEventType = getNextEventType();

  if (!nextEventType) {
    return null;
  }

  return (
    <Button
      data-testid={testIds.shipmentEventLogNextEvent}
      variant="outlined"
      disabled={disabled}
      onClick={(event) => {
        event.stopPropagation();
        onShipmentEventLog(nextEventType);
      }}
    >
      {labelByEventType[nextEventType]}
    </Button>
  );
};

export default ShipmentEventLogButton;
